import { Injectable } from '@angular/core';
import { Observable, defer, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

/**
 * THE OWNER OF HISTORICAL TELEMETRY, for the life of a project selection.
 *
 * Why this exists
 * ---------------
 * Historical reads were guarded by a map of IN-FLIGHT requests that deleted its
 * entry the moment a request settled. That collapses two callers who happen to
 * overlap in time and nothing else — so it is a CONCURRENCY guard being asked to
 * do a CACHING job, and it misses every duplicate that is merely sequential.
 *
 * That is not hypothetical. The open-project path was being scheduled twice, ~1 s
 * apart, with byte-identical parameters: MSAL returns to InteractionStatus.None
 * after every silent token acquisition, the readiness subscription re-ran the
 * whole auth setup on each one, and each run scheduled another project open. The
 * two opens were sometimes concurrent (one request, but two payload deliveries)
 * and sometimes sequential (two requests) depending on token timing, which is
 * why the duplicate count on the wire looked non-deterministic.
 *
 * The scheduling defect is fixed at its source. This exists so that the fix does
 * not have to be the only thing standing between us and a duplicate download:
 * ANY second asker for the same data, from any path, at any distance in time,
 * gets the payload we already hold.
 *
 * Lifetime
 * --------
 * An entry lives until the selection changes or something explicitly invalidates
 * it. That is the deliberate difference from the in-flight map: a project's
 * backfill for a given window and resolution is the same bytes no matter when it
 * is asked for, so re-downloading it is pure waste. Cross-tab navigation becomes
 * an in-memory hit rather than a refetch.
 *
 * Freshness belongs to the live stream, not here. Realtime polls carry
 * isRealtime=true, never reach this, and are what advance the data. The one case
 * that genuinely needs the history AGAIN — a chart scrubbed below the retention
 * window, or the curing baseline — calls invalidate() and re-asks.
 *
 * Errors are never cached: a failed request removes its own entry so the next
 * asker retries rather than inheriting the failure for the whole selection.
 */
@Injectable({ providedIn: 'root' })
export class HistoricalCacheService {
  private entries = new Map<string, Observable<unknown>>();
  private project = '';

  private hitCount = 0;
  private missCount = 0;

  /**
   * Point the cache at a selection. Changing projects drops everything held for
   * the previous one — different project, different bytes, and holding them
   * would be a slow leak across a shift's worth of project switching.
   *
   * Self-guarding on the name, so callers may call it on every emission of a
   * project subject without having to know whether it actually changed.
   */
  selectProject(project: string): void {
    if (project === this.project) {
      return;
    }
    this.project = project;
    this.clear('project changed to ' + (project || '(none)'));
  }

  /**
   * Fetch `key` at most once per selection.
   *
   * `source` is a factory rather than an observable so that a cache hit does not
   * even construct the request — building HttpParams reads identity and tenant
   * state, and doing that for a call we will not make is misleading when it
   * shows up in a trace.
   */
  share<T>(key: string, source: () => Observable<T>): Observable<T> {
    // A REALTIME POLL IS THE FRESHNESS MECHANISM AND CAN NEVER BE CACHED.
    //
    // Holding one for the life of the selection would pin the live chart to
    // whatever the first poll returned — the data would simply stop moving. Two
    // loaders did route their realtime path through the old in-flight map, where
    // it was harmless because the entry was deleted the instant the request
    // settled; under selection-lifetime caching the same code would have frozen
    // live zone and loss updates outright.
    //
    // Enforced HERE rather than at the call sites, because a guard is only as
    // good as the list of callers and that list is exactly what went wrong last
    // time. isRealtime is on the key, so this cannot be got wrong quietly.
    if (key.includes('isRealtime=true')) {
      console.warn(
        `HISTORICAL-CACHE refusing to cache a realtime request: ${this.shortKey(key)}`
      );
      return source();
    }

    const existing = this.entries.get(key) as Observable<T> | undefined;
    if (existing) {
      this.hitCount++;
      console.log(`HISTORICAL-CACHE hit (${this.hitCount} hits) ${this.shortKey(key)}`);
      return this.counted(key, existing);
    }

    this.missCount++;

    // KEPT AS A TRIPWIRE, NOT A TRACE. The per-subscription logging that lived
    // here answered "one loadX call, two GETs" and that question is closed; what
    // stays is the one line that would fire if sharing ever stopped holding,
    // because a second subscription to a shared entry re-runs a COLD http.get
    // and silently doubles a request.
    const deferred = defer(() => {
      const n = (this.sourceSubscribes[key] = (this.sourceSubscribes[key] ?? 0) + 1);
      if (n > 1) {
        console.error(
          `HTTP-SOURCE-RESUBSCRIBE ${this.shortKey(key)} subscribed ${n} times — ` +
            `sharing is not holding for this key`
        );
      }
      return source();
    });

    const shared = deferred.pipe(
      catchError((err) => {
        // A failure must not be remembered for the rest of the selection.
        this.entries.delete(key);
        return throwError(() => err);
      }),
      // refCount: false so the payload survives the last unsubscribe. A tab that
      // opens, reads and closes must not evict the data the next tab needs —
      // that would reintroduce the refetch this class exists to remove.
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.entries.set(key, shared);
    return this.counted(key, shared);
  }

  /**
   * Drop cached data so the next ask refetches.
   *
   * With no argument, drops everything. With a predicate, drops the matching
   * keys — enough to invalidate one family without discarding the others, which
   * is what a scrub-past-retention on one chart should cost.
   */
  invalidate(match?: (key: string) => boolean, reason = ''): number {
    if (!match) {
      const n = this.entries.size;
      this.clear(reason || 'invalidate all');
      return n;
    }
    let dropped = 0;
    for (const key of [...this.entries.keys()]) {
      if (match(key)) {
        this.entries.delete(key);
        dropped++;
      }
    }
    if (dropped > 0) {
      console.log(`HISTORICAL-CACHE invalidated ${dropped} entr(ies) ${reason}`);
    }
    return dropped;
  }

  private clear(reason: string): void {
    if (this.entries.size > 0) {
      console.log(`HISTORICAL-CACHE cleared ${this.entries.size} entr(ies): ${reason}`);
    }
    this.entries.clear();
  }

  /** Subscriptions that actually reached the underlying request, per key. */
  private sourceSubscribes: Record<string, number> = {};
  /** Subscriptions to what this service handed out, per key. */
  private outerSubscribes: Record<string, number> = {};

  /** Count subscribers to a shared entry; silent unless something asks. */
  private counted<T>(key: string, o: Observable<T>): Observable<T> {
    return o.pipe(
      tap({
        subscribe: () => {
          this.outerSubscribes[key] = (this.outerSubscribes[key] ?? 0) + 1;
        },
      })
    );
  }

  /** Per-key subscription counts, for assertions. */
  subscriptionCounts(): { source: Record<string, number>; outer: Record<string, number> } {
    return { source: { ...this.sourceSubscribes }, outer: { ...this.outerSubscribes } };
  }

  /** Entries currently held. Exposed for assertions and diagnostics. */
  get size(): number {
    return this.entries.size;
  }

  /** True when this exact request is already held. */
  has(key: string): boolean {
    return this.entries.has(key);
  }

  /**
   * Hits and misses since load.
   *
   * The invariant worth watching is misses-per-family-per-open: it should be 1.
   * Counting here rather than at the call sites means a path added later is
   * counted too, which is exactly how the previous duplicate stayed invisible.
   */
  stats(): { hits: number; misses: number; size: number } {
    return { hits: this.hitCount, misses: this.missCount, size: this.entries.size };
  }

  /** Endpoint plus the parameters that distinguish one read from another. */
  private shortKey(key: string): string {
    const [url, query = ''] = key.split('?');
    const endpoint = url.split('/').pop() ?? url;
    const keep = ['resolution', 'windowStart', 'windowEnd', 'isRealtime'];
    const parts = query
      .split('&')
      .filter((p) => keep.some((k) => p.startsWith(k + '=')));
    return parts.length ? `${endpoint} ${parts.join(' ')}` : endpoint;
  }
}
